import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import guestReviewApi from '../services/guestReviewApi';

// The Reviews page queue and the menu counter (tasks/post-checkout-review-and-replies.md L27, L29).
// Both are SERVER verdicts: every action invalidates them rather than patching a row locally — a
// client-side guess at the new state is how a surface starts disagreeing with the server.

export const GUEST_REVIEW_QUEUE_KEY = ['guest-reviews-queue'];
export const GUEST_REVIEW_COUNTS_KEY = ['guest-reviews-counts'];

// Stable empty shape so the page never crashes before the first fetch or on an error. Switches
// default to OFF: a pre-fetch state must never offer a send button.
const EMPTY_QUEUE = {
  counts: {
    needsApproval: 0, needsDecision: 0, late: 0, unverified: 0,
    replies: { needsApproval: 0, needsDecision: 0, notPublished: 0 },
    badge: 0
  },
  switches: {
    reviewSend: false, thankYouSend: false, replySend: false, replyDraft: false, triage: false, analysis: false, followUpSend: false,
    replyPlatforms: { airbnb: false, booking: false }
  },
  late: [],
  items: [],
  replies: []
};

// In flight = seconds, not minutes. A draft (written by the server's 5-minute sweep) and a sent reply
// waiting for the platform (up to ~48 h on Booking.com) are NOT in flight — the normal poll finds them.
const isSending = (queue) =>
  (queue?.items || []).some(i => i.review?.status === 'sending' || i.thankYou?.status === 'sending') ||
  (queue?.replies || []).some(r => r.status === 'sending' || r.followUp?.status === 'sending');

export const useGuestReviewQueue = () => {
  const result = useQuery({
    queryKey: GUEST_REVIEW_QUEUE_KEY,
    queryFn: async () => (await guestReviewApi.getQueue()).data,
    // Sends are local-first and drafts are written by the server's 5-minute sweep: poll quickly only
    // while something is in flight. A send whose result never lands is handed to a person by the sweep
    // within minutes, which ends the fast poll.
    refetchInterval: (query) => (isSending(query.state.data) ? 3000 : 60 * 1000),
    staleTime: 15 * 1000
  });
  return { ...result, queue: result.data ?? EMPTY_QUEUE };
};

/**
 * «صوت الضيف» — read only while its tab is open. No fast poll: reviews arrive a few a day and are read by the
 * server's 5-minute sweep; a minute is plenty. `unit` = the unit in the URL, or the server's default.
 */
export const useGuestVoice = (unit, enabled) => useQuery({
  queryKey: ['guest-voice', unit || null],
  queryFn: async () => (await guestReviewApi.getVoice(unit)).data,
  enabled: Boolean(enabled),
  refetchInterval: 60 * 1000,
  staleTime: 30 * 1000,
  placeholderData: (previous) => previous
});

/** The menu counter. `enabled` only for ops roles — the endpoint refuses everyone else. */
export const useGuestReviewCounts = (enabled) => useQuery({
  queryKey: GUEST_REVIEW_COUNTS_KEY,
  queryFn: async () => (await guestReviewApi.getCounts()).data.counts,
  enabled: Boolean(enabled),
  refetchInterval: 2 * 60 * 1000,
  staleTime: 60 * 1000
});

// onSettled, not onSuccess: a refusal usually means the page was stale (someone else sent it), which
// is exactly when a refresh is most needed. The promise is RETURNED so the action stays pending until
// the fresh list is in — no button is live on a stale row between the click and the refresh.
const useAction = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: GUEST_REVIEW_QUEUE_KEY }),
      queryClient.invalidateQueries({ queryKey: GUEST_REVIEW_COUNTS_KEY })
    ])
  });
};

export const useEditReview = () => useAction(guestReviewApi.editReview);
export const useSendReview = () => useAction(guestReviewApi.sendReview);
export const useMarkAlreadyReviewed = () => useAction(guestReviewApi.markAlreadyReviewed);
export const useEditThankYou = () => useAction(guestReviewApi.editThankYou);
export const useSendThankYou = () => useAction(guestReviewApi.sendThankYou);
export const useConfirmThankYouSent = () => useAction(guestReviewApi.confirmThankYouSent);
export const useEditReply = () => useAction(guestReviewApi.editReply);
export const useSendReply = () => useAction(guestReviewApi.sendReply);
export const useSkipReply = () => useAction(guestReviewApi.skipReply);
export const useConfirmReplyPublished = () => useAction(guestReviewApi.confirmReplyPublished);
export const useEditFollowUp = () => useAction(guestReviewApi.editFollowUp);
export const useSendFollowUp = () => useAction(guestReviewApi.sendFollowUp);
export const useSkipFollowUp = () => useAction(guestReviewApi.skipFollowUp);
export const useConfirmFollowUpSent = () => useAction(guestReviewApi.confirmFollowUpSent);
